/**
 * Sidebar v2 snooze/settle classification over forge's thread types.
 *
 * Ported from pingdotgg/t3code
 * `packages/client-runtime/src/state/threadSettled.ts` (effectiveSnoozed,
 * threadRaisedHandWhileSnoozed, canSnooze, threadWokeAt,
 * resolveSnoozePresets, snoozeWakeLabel, resolveCustomSnooze) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: the in-tree vendored copy is pinned
 * below the upstream pin and its shell picks are nominal over the vendored
 * contracts, which forge thread summaries do not satisfy, so the same rules
 * are re-typed against forge's SidebarThreadSummary here. Semantics are the
 * upstream ones; the queue-turn and auto-settle blocks live in forge's own
 * threadSettled module and are untouched.
 */
import type { SidebarThreadSummary } from "~/types";

type SessionLike = SidebarThreadSummary["session"];

interface SnoozeShellLike {
  readonly snoozedUntil: string | null;
  readonly snoozedAt: string | null;
  readonly hasPendingApprovals: boolean;
  readonly hasPendingUserInput: boolean;
  readonly session: SessionLike;
  readonly latestTurn: SidebarThreadSummary["latestTurn"];
}

/** A snoozed thread "raises its hand" when something happens that outranks
 * the user's snooze: the agent is blocked on them, the session failed, or a
 * run completed after the snooze was set. Raising a hand never clears the
 * snooze stamps; it only stops the thread from CLASSIFYING as snoozed. */
// fallow-ignore-next-line complexity -- byte-faithful upstream raised-hand rules (pin e3e7cc3f)
function threadRaisedHandWhileSnoozed(shell: SnoozeShellLike): boolean {
  if (shell.hasPendingApprovals || shell.hasPendingUserInput) return true;
  // Only a FRESH failure raises the hand: a thread snoozed while already
  // failed stays snoozed. session.updatedAt stamps the status edge, so an
  // error newer than the snooze is new information.
  if (
    shell.session?.status === "error" &&
    (shell.snoozedAt == null ||
      (shell.session.updatedAt !== null &&
        Date.parse(shell.session.updatedAt) > Date.parse(shell.snoozedAt)))
  ) {
    return true;
  }
  if (
    shell.snoozedAt != null &&
    shell.latestTurn !== null &&
    shell.latestTurn.state === "completed" &&
    shell.latestTurn.completedAt != null &&
    Date.parse(shell.latestTurn.completedAt) > Date.parse(shell.snoozedAt)
  ) {
    return true;
  }
  return false;
}

/** Snoozed resolution: hidden from the inbox while the wake time is in the
 * future and the thread has not raised its hand. Timer wakes are derived —
 * the stale fields simply stop classifying as snoozed. */
export function effectiveSnoozed(
  shell: SnoozeShellLike,
  options: { readonly now: string },
): boolean {
  if (shell.snoozedUntil == null) return false;
  const wakeAtMs = Date.parse(shell.snoozedUntil);
  // Malformed data never hides a thread.
  if (Number.isNaN(wakeAtMs)) return false;
  if (wakeAtMs <= Date.parse(options.now)) return false;
  return !threadRaisedHandWhileSnoozed(shell);
}

/** A thread may be snoozed unless the agent is blocked on the user: hiding a
 * pending approval or user-input request defeats the request. A running
 * session IS snoozable — snooze only affects visibility, never the agent. */
export function canSnooze(
  shell: Pick<SnoozeShellLike, "hasPendingApprovals" | "hasPendingUserInput">,
): boolean {
  if (shell.hasPendingApprovals || shell.hasPendingUserInput) return false;
  return true;
}

/** When a previously-snoozed thread woke, or null if it never snoozed / is
 * still snoozed. Timer wakes report the wake time itself; raised-hand wakes
 * report the triggering timestamp so a visit BEFORE the early wake does not
 * suppress the indicator. */
export function threadWokeAt(
  shell: SnoozeShellLike,
  options: { readonly now: string },
): string | null {
  if (effectiveSnoozed(shell, options)) return null;
  const wakeMs = Date.parse(shell.snoozedUntil!);
  const raisedAt = raisedHandAt(shell);
  if (raisedAt !== null && Date.parse(raisedAt) < wakeMs) return raisedAt;
  return shell.snoozedUntil;
}

/** Resolve a snoozed thread attention timestamp from pending input, a newer error, or a newer completion. */
// fallow-ignore-next-line complexity -- byte-faithful upstream raised-hand rules (pin e3e7cc3f)
function raisedHandAt(shell: SnoozeShellLike): string | null {
  if (shell.hasPendingApprovals || shell.hasPendingUserInput) return shell.snoozedUntil;
  if (
    shell.session?.status === "error" &&
    shell.session.updatedAt != null &&
    (shell.snoozedAt == null || Date.parse(shell.session.updatedAt) > Date.parse(shell.snoozedAt))
  ) {
    return shell.session.updatedAt;
  }
  if (
    shell.snoozedAt != null &&
    shell.latestTurn !== null &&
    shell.latestTurn.state === "completed" &&
    shell.latestTurn.completedAt != null &&
    Date.parse(shell.latestTurn.completedAt) > Date.parse(shell.snoozedAt)
  ) {
    return shell.latestTurn.completedAt;
  }
  return null;
}

const HOUR_MS = 60 * 60 * 1_000;
const DAY_MS = 24 * HOUR_MS;
const EVENING_HOUR = 18;
const MORNING_HOUR = 9;

export type SnoozePresetId = "hour" | "three-hours" | "evening" | "tomorrow" | "next-week";

export interface SnoozePreset {
  readonly id: SnoozePresetId;
  readonly label: string;
  readonly whenLabel: string;
  readonly snoozedUntil: string;
}

/** Format a wake time as zero-padded local hours and minutes. */
function snoozeTimeOfDayLabel(date: Date): string {
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

/** Find the next occurrence of a local hour, advancing one calendar day if that hour has passed. */
function snoozeAtHour(now: Date, hour: number): Date {
  const date = new Date(now);
  date.setHours(hour, 0, 0, 0);
  if (date.getTime() <= now.getTime()) date.setDate(date.getDate() + 1);
  return date;
}

/** Copy a date and advance it by local calendar days, preserving its local time across offset changes. */
function addSnoozeDays(now: Date, days: number): Date {
  const date = new Date(now);
  date.setDate(date.getDate() + days);
  return date;
}

/** Presets resolve per opening: 1h, 3h, this evening (18:00), tomorrow
 * (09:00), and next Monday 09:00, all DST-safe calendar math. */
export function resolveSharedSnoozePresets(now: Date): ReadonlyArray<SnoozePreset> {
  const inAnHour = new Date(now.getTime() + HOUR_MS);
  const inThreeHours = new Date(now.getTime() + 3 * HOUR_MS);
  const presets: SnoozePreset[] = [
    {
      id: "hour",
      label: "In 1 hour",
      whenLabel: snoozeTimeOfDayLabel(inAnHour),
      snoozedUntil: inAnHour.toISOString(),
    },
    {
      id: "three-hours",
      label: "In 3 hours",
      whenLabel: snoozeTimeOfDayLabel(inThreeHours),
      snoozedUntil: inThreeHours.toISOString(),
    },
  ];

  const evening = snoozeAtHour(now, EVENING_HOUR);
  if (evening.getTime() - now.getTime() > HOUR_MS) {
    presets.push({
      id: "evening",
      label: "This evening",
      whenLabel: snoozeTimeOfDayLabel(evening),
      snoozedUntil: evening.toISOString(),
    });
  }

  const tomorrow = snoozeAtHour(addSnoozeDays(now, 1), MORNING_HOUR);
  presets.push({
    id: "tomorrow",
    label: "Tomorrow",
    whenLabel: snoozeTimeOfDayLabel(tomorrow),
    snoozedUntil: tomorrow.toISOString(),
  });

  const daysUntilMonday = (1 - now.getDay() + 7) % 7 || 7;
  const nextWeek = snoozeAtHour(addSnoozeDays(now, daysUntilMonday), MORNING_HOUR);
  presets.push({
    id: "next-week",
    label: "Next week",
    whenLabel: `${nextWeek.toLocaleDateString(undefined, { weekday: "short" })} ${snoozeTimeOfDayLabel(nextWeek)}`,
    snoozedUntil: nextWeek.toISOString(),
  });

  return presets;
}

/** Compact "wakes in" label for snoozed rows: "2h", "18h", "3d". Minutes
 * round up so a snooze never reads "0m" while still hidden. */
export function snoozeWakeLabel(snoozedUntil: string, options: { readonly now: string }): string {
  const wakeMs = Date.parse(snoozedUntil);
  const nowMs = Date.parse(options.now);
  if (Number.isNaN(wakeMs) || Number.isNaN(nowMs)) return "now";
  const remainingMs = wakeMs - nowMs;
  if (remainingMs <= 0) return "now";
  if (remainingMs < HOUR_MS) return `${Math.max(1, Math.ceil(remainingMs / 60_000))}m`;
  if (remainingMs < DAY_MS) return `${Math.ceil(remainingMs / HOUR_MS)}h`;
  return `${Math.ceil(remainingMs / DAY_MS)}d`;
}

export type CustomSnoozeInput =
  | { readonly mode: "date"; readonly date: string; readonly time: string }
  | {
      readonly mode: "duration";
      readonly amount: string;
      readonly unit: "minutes" | "hours" | "days";
    };

/** Resolve the custom snooze dialog's input to a future ISO stamp, or null
 * when the date/time is malformed or in the past, or the duration is not
 * positive. */
export function resolveCustomSnooze(input: CustomSnoozeInput, now: Date): string | null {
  let wakeMs: number;
  if (input.mode === "date") {
    const parsed = Date.parse(`${input.date}T${input.time || "00:00"}`);
    if (Number.isNaN(parsed)) return null;
    wakeMs = parsed;
  } else {
    const amount = Number(input.amount);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    const unitMs = input.unit === "minutes" ? 60_000 : input.unit === "hours" ? HOUR_MS : DAY_MS;
    wakeMs = now.getTime() + amount * unitMs;
  }
  if (wakeMs <= now.getTime()) return null;
  return new Date(wakeMs).toISOString();
}
